import { type FC, useState } from "react";

import { useDeleteSpecialPriceMutation } from "src/api";
import { Button, Grid, Modal } from "src/components";
import { useModal } from "src/modal";
import { useMutationNotification } from "src/notifications";
import { routes } from "src/routes";
import { useTranslation } from "src/translations";
import { SpecialPriceSourceEnum } from "src/types";
import { SPECIAL_PRICES_FORM_MODAL_WIDTH } from "src/utils";
import { createSpecialPriceActionRow, overrideSpecialPriceRowProps, SpecialPriceAttachment, SpecialPriceForm } from "../../../containers";
import { SHIP_TO_SPECIAL_PRICES_ATTACHMENT_MODAL, SHIP_TO_SPECIAL_PRICES_FORM_MODAL } from "./constants";
import { tableModule } from "./table";

export const ShipToSpecialPrices: FC = () => {
    const { t } = useTranslation();
    const [isFormModalOpened, openFormModal, closeFormModal] = useModal(SHIP_TO_SPECIAL_PRICES_FORM_MODAL);
    const [isAttachmentModalOpened, openAttachmentModal, closeAttachmentModal] = useModal(SHIP_TO_SPECIAL_PRICES_ATTACHMENT_MODAL);
    const [specialPriceIdForEdit, setSpecialPriceIdForEdit] = useState<number | undefined>(undefined);
    const { customerId } = routes.customerDetail.useParams();
    const [deleteSpecialPrice, deleteSpecialPriceResult] = useDeleteSpecialPriceMutation();

    useMutationNotification(deleteSpecialPriceResult, "specialPrices.delete.success", "specialPrices.delete.error");

    const createSpecialPrice = () => {
        setSpecialPriceIdForEdit(undefined);
        openFormModal();
    };

    const editSpecialPrice = (specialPriceId: number) => {
        setSpecialPriceIdForEdit(specialPriceId);
        openFormModal();
    };

    const openSpecialPriceAttachment = (specialPriceId: number) => {
        setSpecialPriceIdForEdit(specialPriceId);
        openAttachmentModal();
    };

    return customerId ? (
        <Grid gridAutoFlow="row" gap="2rem">
            <Grid justifyContent="end">
                <Button type="button" onClick={createSpecialPrice}>
                    {t("specialPrices.createNew")}
                </Button>
            </Grid>
            <tableModule.Container
                overrideRowProps={overrideSpecialPriceRowProps}
                isExpandable
                actions={createSpecialPriceActionRow(
                    t,
                    editSpecialPrice,
                    deleteSpecialPrice,
                    openSpecialPriceAttachment,
                    SpecialPriceSourceEnum.ShipTo,
                )}
                additionalQueryParams={{ customerId }}
                defaultTableConfig={{ initialFilters: { status: "ACTIVE" } }}
            />
            <SpecialPriceAttachment
                isOpened={isAttachmentModalOpened}
                onClose={closeAttachmentModal}
                customerId={customerId}
                specialPriceId={specialPriceIdForEdit}
            />
            <Modal
                title={specialPriceIdForEdit ? t("specialPrices.modal.updateTitle") : t("specialPrices.modal.createTitle")}
                opened={isFormModalOpened}
                onClose={closeFormModal}
                maxWidth={SPECIAL_PRICES_FORM_MODAL_WIDTH}
            >
                <SpecialPriceForm customerId={customerId} closeModal={closeFormModal} specialPriceId={specialPriceIdForEdit} />
            </Modal>
        </Grid>
    ) : null;
};
